import { useCallback, useEffect } from 'react';
import { JS_LOG_EVENT_NAME } from '../context/initializeJsLogger';
import { JsLogEvent, LogContext, LogLevel } from '../types';

/**
 * Props for the useBroadcastJsLoggerEvents hook
 */
interface UseBroadcastJsLoggerEventsProps {
  enabled?: boolean;
  onJsLogReceived: (message: string, level: LogLevel, context: LogContext) => void;
}

/**
 * Hook that listens for custom logging events and processes them through the provided callback
 * @param props - The hook props containing the onLogEvent callback
 */
export const useBroadcastJsLoggerEvents = ({
  onJsLogReceived,
  enabled = false
}: UseBroadcastJsLoggerEventsProps): void => {
  const handleLogEvent = useCallback(
    (logEvent: JsLogEvent) => {
      const { detail } = logEvent;
      const { app, level, message, context } = detail;
      // 'app' is not a valid Grafana log property, so we move it to the context
      // so it can be accessed by the beforeSend callback prior to sending the log to the remote service
      const contextWithApp = { ...context, app };
      if (enabled) {
        onJsLogReceived(message, level, contextWithApp);
      }
    },
    [onJsLogReceived, enabled]
  );

  useEffect(() => {
    // Add event listener for custom logging events
    window.addEventListener(JS_LOG_EVENT_NAME, handleLogEvent as EventListener);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener(JS_LOG_EVENT_NAME, handleLogEvent as EventListener);
    };
  }, [handleLogEvent, enabled]);
};
